import { useContext } from 'react';
import { IntlDefaultMessageContext, LocalizedText } from '@mews/framework';
import { createHandler, lowerFirst } from '@mews/std';
import { OnboardingAuditFieldIdentifier } from '@mews-commander/data-access';
import { OnboardingTextKey } from '../../OnboardingTextKey';

const nameToTextKey = createHandler<OnboardingAuditFieldIdentifier, Mews.TextKey | null>(
    {
        AccountingCategoriesSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionAccountingCategoriesSetUp,
        AccountingConfigurationSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionAccountingConfigurationSetUp,
        AdditionalServiceAccountingCategoriesSetUp:
            OnboardingTextKey.GoLiveAuditsTaskDescriptionAdditionalServiceAccountingCategoriesSetUp,
        AdditionalServiceProductAccountingCategoriesSetUp:
            OnboardingTextKey.GoLiveAuditsTaskDescriptionAdditionalServiceProductAccountingCategoriesSetUp,
        AdditionalServiceProductTaxesSetUp:
            OnboardingTextKey.GoLiveAuditsTaskDescriptionAdditionalServiceProductTaxesSetUp,
        AdditionalServicesSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionAdditionalServicesSetUp,
        AnalyticsSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionAnalyticsSetUp,
        BillAndInvoiceCountersConfigured: OnboardingTextKey.GoLiveAuditsTaskDescriptionBillAndInvoiceCountersConfigured,
        BillHeaderAndFooterSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionBillHeaderAndFooterSetUp,
        BookableProductTaxesSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionBookableProductTaxesSetUp,
        BookableRatesSetup: OnboardingTextKey.GoLiveAuditsTaskDescriptionBookableRatesSetup,
        BookableServiceProductAccountingCategoriesSetUp:
            OnboardingTextKey.GoLiveAuditsTaskDescriptionBookableServiceProductAccountingCategoriesSetUp,
        BookableServiceProductCategoriesSetUp:
            OnboardingTextKey.GoLiveAuditsTaskDescriptionBookableServiceProductCategoriesSetUp,
        BookableServiceProductsSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionBookableServiceProductsSetUp,
        BookableServicesAccountingSettingsSetUp:
            OnboardingTextKey.GoLiveAuditsTaskDescriptionBookableServicesAccountingSettingsSetUp,
        BookableServicesGeneralSettingsSetUp:
            OnboardingTextKey.GoLiveAuditsTaskDescriptionBookableServicesGeneralSettingsSetUp,
        BookingEnginesAdded: OnboardingTextKey.GoLiveAuditsTaskDescriptionBookingEnginesAdded,
        CancellationPoliciesSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionCancellationPoliciesSetUp,
        CashiersAssigned: OnboardingTextKey.GoLiveAuditsTaskDescriptionCashiersAssigned,
        ChannelManagerConfigured: OnboardingTextKey.GoLiveAuditsTaskDescriptionChannelManagerConfigured,
        CompanyProfilesAdded: OnboardingTextKey.GoLiveAuditsTaskDescriptionCompanyProfilesAdded,
        DepartmentsAdded: OnboardingTextKey.GoLiveAuditsTaskDescriptionDepartmentsAdded,
        EhwSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionEhwSetUp,
        EmailTemplatesCustomized: OnboardingTextKey.GoLiveAuditsTaskDescriptionEmailTemplatesCustomized,
        EmployeeMfaSetup: OnboardingTextKey.GoLiveAuditsTaskDescriptionEmployeeMfaSetup,
        EmployeesAdded: OnboardingTextKey.GoLiveAuditsTaskDescriptionEmployeesAdded,
        EmployeesAssignedToDepartments: OnboardingTextKey.GoLiveAuditsTaskDescriptionEmployeesAssignedToDepartments,
        ExternalPaymentTypesSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionExternalPaymentTypesSetUp,
        GatewayPaymentTypesMapped: OnboardingTextKey.GoLiveAuditsTaskDescriptionGatewayPaymentTypesMapped,
        KeyCuttersSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionKeyCuttersSetUp,
        MewsTerminalPaymentTypesMapped: OnboardingTextKey.GoLiveAuditsTaskDescriptionMewsTerminalPaymentTypesMapped,
        PaymentAutomationEnabled: OnboardingTextKey.GoLiveAuditsTaskDescriptionPaymentAutomationEnabled,
        PrintersSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionPrintersSetUp,
        ProductRulesSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionProductRulesSetUp,
        PropertyLogoAndSignInImageUploaded:
            OnboardingTextKey.GoLiveAuditsTaskDescriptionPropertyLogoAndSignInImageUploaded,
        PropertySettingsCompleted: OnboardingTextKey.GoLiveAuditsTaskDescriptionPropertySettingsCompleted,
        QrCodesSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionQrCodesSetUp,
        RateGroupsSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionRateGroupsSetUp,
        RequireAccountingCategorySetupActive:
            OnboardingTextKey.GoLiveAuditsTaskDescriptionRequireAccountingCategorySetupActive,
        RestrictionsAdded: OnboardingTextKey.GoLiveAuditsTaskDescriptionRestrictionsAdded,
        RoutingRulesSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionRoutingRulesSetUp,
        ServicePromotionsSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionServicePromotionsSetUp,
        SettlementRulesSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionSettlementRulesSetUp,
        TasksCompleted: OnboardingTextKey.GoLiveAuditsTaskDescriptionTasksCompleted,
        TerminalsSetUp: OnboardingTextKey.GoLiveAuditsTaskDescriptionTerminalsSetUp,
        TravelAgentContractsAdded: OnboardingTextKey.GoLiveAuditsTaskDescriptionTravelAgentContractsAdded,
        UnpaidInvoiceOverview: OnboardingTextKey.GoLiveAuditsTaskDescriptionUnpaidInvoiceOverview,
    },
    null
);

/** Renders a localized AuditName
 *
 * Since Audit Names are driven from the API, we take special care to handle
 * fallbacks to the defaultMessage, the same way that useLocalizeText does,
 * but we _do_ want to pretend the name is a TextKey rather than render an
 * empty string.
 */
export const AuditName = ({ fieldIdentifier }: { fieldIdentifier: OnboardingAuditFieldIdentifier }) => {
    const getDefaultMessage = useContext(IntlDefaultMessageContext);

    const textKey = nameToTextKey(fieldIdentifier);

    if (textKey === null) {
        const toLowerTextKey = lowerFirst(fieldIdentifier) as Mews.TextKey;
        return <span>{getDefaultMessage(toLowerTextKey)}</span>;
    }

    return <LocalizedText textKey={textKey} />;
};
